import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { EmptyState, FilterPills } from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { PageHeader } from "src/grading/layout/PageHeader";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { selectCurrentScopes } from "src/store/reducers/auth/authSlice";
import { useGetMyClassesQuery } from "src/store/reducers/classes/classesApiSlice";
import { useListSchoolsQuery } from "src/store/reducers/districts/districtsApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";

type Scope = "class" | "school" | "district";

/** Entry to the class, school and district rollups the caller may read (FR-061, FR-062). */
const ReportsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { hasPermissions } = usePermissions();
  const scopes = useSelector(selectCurrentScopes);
  // The Class scope lists the caller's own classes, so it needs `classes:own` too. Admins report on
  // a school or the district here and open a class report from the class itself.
  const canClass =
    hasPermissions(
      permissions.READ_CLASS_REPORTS.action,
      permissions.READ_CLASS_REPORTS.subject,
    ) &&
    hasPermissions(
      permissions.READ_OWN_CLASSES.action,
      permissions.READ_OWN_CLASSES.subject,
    );
  const canSchool = hasPermissions(
    permissions.READ_SCHOOL_REPORTS.action,
    permissions.READ_SCHOOL_REPORTS.subject,
  );
  const canDistrict = hasPermissions(
    permissions.READ_DISTRICT_REPORTS.action,
    permissions.READ_DISTRICT_REPORTS.subject,
  );
  const { data: classes } = useGetMyClassesQuery(undefined, {
    skip: !canClass,
  });
  const { data: schools } = useListSchoolsQuery(scopes?.districtId ?? "", {
    skip: !canDistrict || !scopes?.districtId,
  });
  const [picked, setPicked] = useState<Scope | null>(null);

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  const schoolIds =
    canDistrict && schools
      ? schools.map((s) => s.id)
      : (scopes?.schoolIds ?? []);
  const schoolName = (id: string) =>
    schools?.find((s) => s.id === id)?.name ?? id;

  const available = useMemo(() => {
    const list: { value: Scope; label: string }[] = [];
    if (canClass)
      list.push({
        value: "class",
        label: t("reports.scopeClass", { defaultValue: "Class" }),
      });
    if (canSchool && schoolIds.length > 0)
      list.push({
        value: "school",
        label: t("reports.scopeSchool", { defaultValue: "School" }),
      });
    if (canDistrict && scopes?.districtId)
      list.push({
        value: "district",
        label: t("reports.scopeDistrict", { defaultValue: "District" }),
      });
    return list;
  }, [canClass, canSchool, canDistrict, schoolIds.length, scopes, t]);

  const scope: Scope | null =
    picked && available.some((a) => a.value === picked)
      ? picked
      : (available[0]?.value ?? null);

  return (
    <Box>
      <PageHeader
        title={t("reports.title", { defaultValue: "Reports" })}
        description={t("reports.neverRank", {
          defaultValue:
            "These count students against fixed benchmarks. They never compare students with each other.",
        })}
        actions={
          available.length > 0 ? (
            <FilterPills
              variant="tray"
              aria-label={t("reports.scope", { defaultValue: "Report scope" })}
              options={available}
              value={scope ?? ""}
              onChange={(v) => setPicked(v as Scope)}
            />
          ) : undefined
        }
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
        {available.length > 0 ? null : (
          <EmptyState
            title={t("reports.noScopes", {
              defaultValue: "No reports available",
            })}
            body={t("reports.noScopesBody", {
              defaultValue: "Your role does not include any report scopes.",
            })}
          />
        )}
        {scope === "district" && scopes?.districtId && (
          <Paper sx={{ p: "20px 22px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="h4" component="h2">
                {t("reports.scopeDistrict", { defaultValue: "District" })}
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  history.push(
                    `/grading/reports/districts/${scopes.districtId}`,
                  )
                }
              >
                {t("reports.districtReport", {
                  defaultValue: "District report",
                })}
              </Button>
            </Box>
          </Paper>
        )}
        {scope === "school" && (
          <Paper sx={{ overflowX: "auto" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ px: 2.75, pt: 2, pb: 1 }}
            >
              {t("reports.schools", { defaultValue: "Schools" })}
            </Typography>
            <Table
              size="small"
              aria-label={t("reports.schoolReports", {
                defaultValue: "School reports",
              })}
            >
              <TableBody>
                {schoolIds.map((id) => (
                  <TableRow
                    key={id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      history.push(`/grading/reports/schools/${id}`)
                    }
                  >
                    <TableCell>{schoolName(id)}</TableCell>
                    <TableCell align="right">
                      <Button size="small">
                        {t("reports.schoolReport", {
                          defaultValue: "School report",
                        })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
        {scope === "class" && (
          <Paper sx={{ overflowX: "auto" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ px: 2.75, pt: 2, pb: 1 }}
            >
              {t("reports.myClasses", { defaultValue: "My classes" })}
            </Typography>
            <Table
              size="small"
              aria-label={t("reports.classReports", {
                defaultValue: "Class reports",
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("reports.class", { defaultValue: "Class" })}
                  </TableCell>
                  <TableCell>
                    {t("reports.year", { defaultValue: "Year" })}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(classes ?? []).map((c) => (
                  <TableRow
                    key={c.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      history.push(`/grading/reports/classes/${c.id}`)
                    }
                  >
                    <TableCell>
                      {c.name}
                      {c.period ? ` · Period ${c.period}` : ""}
                    </TableCell>
                    <TableCell>{c.schoolYear}</TableCell>
                    <TableCell align="right">
                      <Button size="small">
                        {t("reports.classReport", {
                          defaultValue: "Class report",
                        })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {classes?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      {t("reports.noClasses", {
                        defaultValue:
                          "You are not assigned to any classes yet.",
                      })}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ReportsPage;
